/**
 * Row Component
 *
 * A layout primitive for horizontal arrangements with consistent spacing.
 * Replaces raw Tailwind like `flex items-center gap-* justify-between`.
 *
 * @example
 * ```tsx
 * import { Row } from '@/ui';
 *
 * <Row gap={4} align="center">
 *   <Icon />
 *   <Text>Label</Text>
 * </Row>
 *
 * <Row justify="between" align="center">
 *   <Heading>Title</Heading>
 *   <Button>Action</Button>
 * </Row>
 * ```
 *
 * @see docs/ui-contract.md
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/utils/utils';

// =============================================================================
// VARIANTS
// =============================================================================

const rowVariants = cva('flex flex-row', {
  variants: {
    /**
     * Gap between items (Tailwind spacing scale)
     */
    gap: {
      0: 'gap-0',
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      5: 'gap-5',
      6: 'gap-6',
      8: 'gap-8',
      10: 'gap-10',
      12: 'gap-12',
      xs: 'gap-2',
      sm: 'gap-3',
      md: 'gap-4',
      lg: 'gap-6',
      xl: 'gap-8',
      '2xl': 'gap-12',
    },
    /**
     * Vertical alignment of items
     */
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline',
    },
    /**
     * Horizontal distribution of items
     */
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    },
    /**
     * Whether the row should wrap on smaller screens
     */
    wrap: {
      true: 'flex-wrap',
      false: 'flex-nowrap',
      reverse: 'flex-wrap-reverse',
    },
    /**
     * Whether the row should fill its container width
     */
    fullWidth: {
      true: 'w-full',
      false: '',
    },
    /**
     * Reverse the order of items
     */
    reverse: {
      true: 'flex-row-reverse',
      false: '',
    },
    /**
     * Internal padding using Tailwind spacing scale
     */
    padding: {
      none: 'p-0',
      xs: 'p-2',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
      xl: 'p-8',
    },
  },
  defaultVariants: {
    gap: 4,
    align: 'center',
    justify: 'start',
    wrap: false,
    fullWidth: false,
    reverse: false,
  },
});

// =============================================================================
// TYPES
// =============================================================================

interface RowProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof rowVariants> {
  /**
   * The HTML element to render as
   * @default 'div'
   */
  as?: 'div' | 'section' | 'article' | 'nav' | 'header' | 'footer' | 'span';
  /**
   * Additional spacing between items at different breakpoints
   */
  responsiveGap?: {
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Row - Horizontal layout primitive
 *
 * Use Row for any horizontal arrangement of elements.
 * This replaces patterns like:
 * - `className="flex items-center gap-4"`
 * - `className="flex justify-between"`
 * - `className="flex flex-wrap gap-2"`
 */
export const Row = React.memo(
  React.forwardRef<HTMLDivElement, RowProps>(
    (
      {
        as: Component = 'div',
        gap,
        padding,
        align,
        justify,
        wrap,
        fullWidth,
        reverse,
        responsiveGap,
        className,
        children,
        ...props
      },
      ref
    ) => {
      // Build responsive gap classes
      const responsiveClasses = responsiveGap
        ? Object.entries(responsiveGap)
          .map(([breakpoint, value]) => `${breakpoint}:gap-${value}`)
          .join(' ')
        : '';

      return (
        <Component
          ref={ref as React.Ref<never>}
          className={cn(
            rowVariants({ gap, padding, align, justify, wrap, fullWidth, reverse }),
            responsiveClasses,
            className
          )}
          data-layout="row"
          {...props}
        >
          {children}
        </Component>
      );
    }
  )
);

Row.displayName = 'Row';

// =============================================================================
// CONVENIENCE COMPONENTS
// =============================================================================

/**
 * HStack - Alias for Row (horizontal stack)
 * Useful for code clarity when mixing with Stack/VStack
 *
 * @deprecated Use `Row` instead. HStack is not used in the codebase and will be removed in v3.0.0
 */
export const HStack = Row;

/**
 * SpaceBetween - Common pattern for header layouts
 */
export function SpaceBetween({
  children,
  className,
  ...props
}: Omit<RowProps, 'justify'>): React.ReactElement {
  return (
    <Row justify="between" align="center" fullWidth className={className} {...props}>
      {children}
    </Row>
  );
}
